import React, { useState } from 'react';
import { CheckCircle2, XCircle, Clock, AlertCircle, User, Calendar, MapPin, MessageSquare, FileText, TrendingUp, Filter } from 'lucide-react';

export default function TaskManagementApprovalCenter() {
  const [selectedTab, setSelectedTab] = useState('pending');
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock data for pending approvals
  const pendingApprovals = [
    {
      id: 'OBS-2025-001',
      title: 'Unsafe ladder positioning at Unit 3',
      reporter: 'John Smith',
      reportDate: '2025-12-05',
      location: 'Plant 1 - Unit 3',
      severity: 'High',
      riskScore: 8.5,
      category: 'Unsafe Act',
      description: 'Worker observed using ladder without proper stabilization on uneven surface',
      recommendedAction: 'Immediate ladder safety training and site inspection',
      images: 2,
      status: 'pending'
    },
    {
      id: 'OBS-2025-002',
      title: 'Missing guard rail on platform',
      reporter: 'Sarah Johnson',
      reportDate: '2025-12-04',
      location: 'Plant 1 - Unit 2',
      severity: 'Critical',
      riskScore: 9.2,
      category: 'Unsafe Condition',
      description: 'Guard rail section missing on elevated platform, creating fall hazard',
      recommendedAction: 'Install temporary barrier and schedule permanent repair',
      images: 3,
      status: 'pending'
    },
    {
      id: 'OBS-2025-003',
      title: 'PPE non-compliance in chemical area',
      reporter: 'Mike Wilson',
      reportDate: '2025-12-03',
      location: 'Plant 2 - Chemical Storage',
      severity: 'Medium',
      riskScore: 6.8,
      category: 'Unsafe Act',
      description: 'Multiple workers not wearing required chemical-resistant gloves',
      recommendedAction: 'Refresher PPE training and increased supervision',
      images: 1,
      status: 'pending'
    }
  ];

  // Mock data for active tasks
  const activeTasks = [
    {
      id: 'TASK-2025-001',
      observationId: 'OBS-2025-004',
      title: 'Repair electrical panel cover',
      assignedTo: 'Maintenance Team A',
      dueDate: '2025-12-10',
      priority: 'High',
      progress: 60,
      status: 'in_progress',
      comments: 3,
      location: 'Plant 1 - Electrical Room'
    },
    {
      id: 'TASK-2025-002',
      observationId: 'OBS-2025-005',
      title: 'Replace damaged fire extinguisher',
      assignedTo: 'Safety Team',
      dueDate: '2025-12-08',
      priority: 'Critical',
      progress: 80,
      status: 'in_progress',
      comments: 5,
      location: 'Plant 2 - Production Floor'
    }
  ];

  // Mock metrics data
  const metrics = {
    pendingApprovals: 12,
    activeTasks: 8,
    completedThisWeek: 15,
    overdueActions: 3,
    avgResolutionTime: '4.2 days',
    completionRate: 87
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Critical: 'bg-red-100 text-red-800 border-red-300',
      High: 'bg-orange-100 text-orange-800 border-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Low: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors?.[severity] || colors?.Medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || colors?.pending;
  };

  const handleApprove = (taskId) => {
    console.log('Approving task:', taskId);
    // Implementation would handle approval logic
  };

  const handleReject = (taskId) => {
    console.log('Rejecting task:', taskId);
    // Implementation would handle rejection logic
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Task Management & Approval Center</h1>
              <p className="text-sm text-gray-600 mt-1">Review and manage corrective actions for unsafe acts and conditions</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
                <Filter size={18} />
                <span>Filters</span>
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                <FileText size={18} />
                <span>Export Report</span>
              </button>
            </div>
          </div>

          {/* Metrics Summary */}
          <div className="grid grid-cols-6 gap-4 mt-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={18} className="text-yellow-600" />
                <span className="text-xs font-medium text-yellow-800">Pending Approvals</span>
              </div>
              <div className="text-2xl font-bold text-yellow-900">{metrics?.pendingApprovals}</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={18} className="text-blue-600" />
                <span className="text-xs font-medium text-blue-800">Active Tasks</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">{metrics?.activeTasks}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={18} className="text-green-600" />
                <span className="text-xs font-medium text-green-800">Completed (Week)</span>
              </div>
              <div className="text-2xl font-bold text-green-900">{metrics?.completedThisWeek}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={18} className="text-red-600" />
                <span className="text-xs font-medium text-red-800">Overdue</span>
              </div>
              <div className="text-2xl font-bold text-red-900">{metrics?.overdueActions}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Calendar size={18} className="text-gray-600" />
                <span className="text-xs font-medium text-gray-800">Avg Resolution</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{metrics?.avgResolutionTime}</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={18} className="text-gray-600" />
                <span className="text-xs font-medium text-gray-800">Completion Rate</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{metrics?.completionRate}%</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-6 border-b border-gray-200">
            <button
              onClick={() => setSelectedTab('pending')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                selectedTab === 'pending' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Pending Approvals ({pendingApprovals?.length})
            </button>
            <button
              onClick={() => setSelectedTab('tasks')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                selectedTab === 'tasks' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Active Tasks ({activeTasks?.length})
            </button>
            <button
              onClick={() => setSelectedTab('timeline')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 ${
                selectedTab === 'timeline' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Timeline View
            </button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="p-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Panel - List View */}
          <div className="col-span-5 space-y-4">
            {selectedTab === 'pending' && pendingApprovals?.map((approval) => (
              <div
                key={approval?.id}
                onClick={() => setSelectedTask(approval)}
                className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedTask?.id === approval?.id ? 'border-blue-500 shadow-md' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-mono text-gray-600">{approval?.id}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(approval?.severity)}`}>
                        {approval?.severity}
                      </span>
                      <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        {approval?.category}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{approval?.title}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-600">{approval?.riskScore}</div>
                    <div className="text-xs text-gray-500">Risk Score</div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <User size={14} />
                    <span>{approval?.reporter}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={14} />
                    <span>{approval?.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar size={14} />
                    <span>{approval?.reportDate}</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{approval?.images} images attached</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(approval?.status)}`}>
                      Awaiting Review
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {selectedTab === 'tasks' && activeTasks?.map((task) => (
              <div
                key={task?.id}
                onClick={() => setSelectedTask(task)}
                className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedTask?.id === task?.id ? 'border-blue-500 shadow-md' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-mono text-gray-600">{task?.id}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(task?.priority)}`}>
                        {task?.priority}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{task?.title}</h3>
                  </div>
                </div>

                <div className="space-y-2 text-sm mb-3">
                  <div className="flex items-center gap-2 text-gray-600">
                    <User size={14} />
                    <span>{task?.assignedTo}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={14} />
                    <span>{task?.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar size={14} />
                    <span>Due: {task?.dueDate}</span>
                  </div>
                </div>

                <div className="mb-2">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>Progress</span>
                    <span className="font-medium">{task?.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${task?.progress}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MessageSquare size={14} />
                    <span className="text-xs">{task?.comments} comments</span>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(task?.status)}`}>
                    In Progress
                  </span>
                </div>
              </div>
            ))}

            {selectedTab === 'timeline' && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Task Timeline View</h3>
                <div className="space-y-4">
                  {/* Timeline visualization would go here */}
                  <div className="text-center text-gray-500 py-8">
                    <Calendar size={48} className="mx-auto mb-2 text-gray-400" />
                    <p>Interactive timeline visualization</p>
                    <p className="text-sm">Showing task lifecycle from assignment to completion</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Detail View */}
          <div className="col-span-7">
            {selectedTask ? (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{selectedTask?.title}</h2>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-600">{selectedTask?.id}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(selectedTask?.severity || selectedTask?.priority)}`}>
                        {selectedTask?.severity || selectedTask?.priority}
                      </span>
                    </div>
                  </div>
                  {selectedTask?.riskScore && (
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-600">{selectedTask?.riskScore}</div>
                      <div className="text-xs text-gray-500">Risk Score</div>
                    </div>
                  )}
                </div>

                {/* Observation Details */}
                {selectedTask?.description && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Observation Details</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Description</label>
                        <p className="text-gray-900 mt-1">{selectedTask?.description}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Reporter</label>
                          <p className="text-gray-900 mt-1">{selectedTask?.reporter}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700">Report Date</label>
                          <p className="text-gray-900 mt-1">{selectedTask?.reportDate}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Location</label>
                        <p className="text-gray-900 mt-1">{selectedTask?.location}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommended Action */}
                {selectedTask?.recommendedAction && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Recommended Action</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-gray-900">{selectedTask?.recommendedAction}</p>
                    </div>
                  </div>
                )}

                {/* Task Assignment (for active tasks) */}
                {selectedTask?.assignedTo && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Task Assignment</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Assigned To</label>
                          <p className="text-gray-900 mt-1">{selectedTask?.assignedTo}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700">Due Date</label>
                          <p className="text-gray-900 mt-1">{selectedTask?.dueDate}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Approval Actions */}
                {selectedTask?.status === 'pending' && (
                  <div className="flex gap-3 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => handleReject(selectedTask?.id)}
                      className="flex-1 px-4 py-3 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2 font-medium"
                    >
                      <XCircle size={20} />
                      <span>Reject & Return</span>
                    </button>
                    <button
                      onClick={() => handleApprove(selectedTask?.id)}
                      className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-medium"
                    >
                      <CheckCircle2 size={20} />
                      <span>Approve & Create Task</span>
                    </button>
                  </div>
                )}

                {/* Communication Panel */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Communication</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        SM
                      </div>
                      <div className="flex-1">
                        <textarea
                          placeholder="Add a comment or request additional information..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          rows="3"
                        />
                        <button className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                          Post Comment
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 text-center">
                      No comments yet
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                <div className="max-w-md mx-auto">
                  <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Task Selected</h3>
                  <p className="text-gray-600">
                    Select a task from the list to view details and take action
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}